import type { Editor } from '@tiptap/core';
import { Fragment, Slice } from '@tiptap/pm/model';
import * as Sentry from '@sentry/react';
import type { EditApplier, PendingEdit } from '@/lib/ai-composer/applier-registry';
import type { EditRequest, EditDeltaData, EditEntityKind } from '@/lib/ai-composer/types';
import { isReplaceTextOp, isEmbedCanvasOp } from '@/lib/ai-composer/types';
import type { WorkspaceService } from '@/lib/workspace/workspace-service';
import { getAiEditing } from '../extensions/ai-editing/ai-editing';
import { aiSuggestionPluginKey } from '../extensions/ai-editing/suggestion-store';
import { aiCaretPluginKey } from '../extensions/ai-editing/ai-caret';
import { blockAt } from './block-actions';
import { markCanvasPending, clearCanvasPending } from './canvas-pending-store';
import { parseHtmlToFragment, isRenderableHtml, inlinePosAtOrBefore } from './html-stream';

// Discriminated PendingEdit so accept/reject can dispatch per op.
export interface TextPending extends PendingEdit {
  kind: 'text';
  from: number;
  to: number;
  text: string;
  /** Doc position where the AI-written content currently ends. The whole range
   *  [from, writtenEnd] is replaced each chunk; the new end comes from the transaction
   *  mapping (NOT the range-tracker decoration, which collapses across block boundaries). */
  writtenEnd: number;
  /** Original PLAIN text of the target range, captured at begin() — feeds the
   *  before/after word diff in review mode. */
  originalText: string;
  /** Original SLICE of the target range (nodes + marks + block structure), captured at
   *  begin() so reject() restores the exact original FORMATTING — not flattened text
   *  (textBetween drops bold/headings/lists). */
  originalSlice: Slice;
}

export interface CanvasPending extends PendingEdit {
  kind: 'canvas';
  canvasId: string | null; // null until applyChunk creates the canvas
  from: number;
  /** Placement uses `to` so a multi-block selection embeds after the LAST block. */
  to: number;
}

export type TiptapPending = TextPending | CanvasPending;

export interface EmbedOps {
  /** Insert the canvas embed AT position `at` (the end of the source block) so the
   *  chart lands AFTER the selected content, leaving the original text intact. */
  insert: (canvasId: string, at: number) => void;
  /** Swap a placeholder canvasId for the real server-created one (reconcile after
   *  the embed was inserted on click with a placeholder). */
  reconcile: (placeholderId: string, realCanvasId: string) => void;
  remove: (canvasId: string) => void;
}

let counter = 0;

export class TiptapEditApplier implements EditApplier {
  private readonly embedOps: EmbedOps;

  constructor(
    private readonly editor: Editor,
    public readonly entityKind: EditEntityKind,
    private readonly workspace?: WorkspaceService,
    /** Override any of insert/remove. Whatever is omitted falls back to the
     *  defaults below. */
    embedOps?: Partial<EmbedOps>,
    /** Schedules the caret's linger-then-clear after streaming ends. Tests inject a
     *  capturing scheduler; production uses setTimeout for a brief fade. */
    private readonly schedule: (cb: () => void, delayMs: number) => unknown =
      (cb, d) => setTimeout(cb, d),
  ) {
    this.embedOps = {
      insert: (canvasId, at) => {
        const safeAt = Math.min(Math.max(at, 0), this.editor.state.doc.content.size);
        this.editor.chain().focus().setTextSelection(safeAt).insertCanvasEmbed(canvasId).run();
      },
      reconcile: (placeholderId, realCanvasId) => {
        const view = this.editor.view;
        let found = false;
        view.state.doc.descendants((node, pos) => {
          if (found) return false;
          if (node.type.name === 'canvasEmbed' && node.attrs['canvasId'] === placeholderId) {
            const tr = view.state.tr.setNodeAttribute(pos, 'canvasId', realCanvasId);
            view.dispatch(tr);
            found = true;
            return false;
          }
        });
      },
      remove: (canvasId) => {
        const { state } = this.editor;
        state.doc.descendants((node, pos) => {
          if (node.type.name === 'canvasEmbed' && node.attrs['canvasId'] === canvasId) {
            this.editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
            return false;
          }
        });
      },
      ...embedOps,
    };
  }

  /** Leave the caret at the end for a beat after streaming stops, then clear it (fade). */
  private lingerThenClearCaret(): void {
    this.schedule(() => getAiEditing(this.editor).clearCaret(), 1200);
  }

  supports(op: 'replace-text' | 'embed-canvas'): boolean {
    return op === 'replace-text' || op === 'embed-canvas';
  }

  begin(req: EditRequest, mode: import('@/lib/ai-composer/types').ReviewMode = 'ghost-write'): TiptapPending {
    if (req.target.kind !== 'tiptap-range') {
      throw new Error('TiptapEditApplier requires a tiptap-range target');
    }
    const id = `pe-${(counter += 1)}`;
    const { from, to } = req.target;

    if (req.action === 'generate-chart') {
      // Insert the embed immediately (on click) with the pending edit id as a
      // placeholder canvasId, marked pending so the node renders the loading
      // skeleton right away. applyChunk reconciles it to the real server canvasId.
      const block = blockAt(this.editor, to);
      const at = block ? block.start + block.node.nodeSize : to;
      markCanvasPending(id);
      this.embedOps.insert(id, at);
      return { kind: 'canvas', id, canvasId: null, from, to };
    }

    // Capture the original so reject() can restore it (in-doc streaming overwrites the
    // original as chunks arrive). originalText (plain) feeds the review diff; originalSlice
    // preserves nodes/marks/blocks so reject restores the exact formatting, not flat text.
    const originalText = this.editor.state.doc.textBetween(from, to, '\n');
    const originalSlice = this.editor.state.doc.slice(from, to);
    // In-doc streaming: set a range-tracker decoration so the live [from,to] maps
    // through concurrent edits. No ghost widget — chunks are written INTO the doc.
    getAiEditing(this.editor).begin({ id, from, to, text: '', mode, streaming: true });

    return { kind: 'text', id, from, to, writtenEnd: to, text: '', originalText, originalSlice };
  }

  /** Read the live [from,to] of a text pending from its range-tracker decoration,
   *  falling back to the pending's last-known positions. */
  private liveRange(pending: TextPending): { from: number; to: number } {
    const set = aiSuggestionPluginKey.getState(this.editor.state);
    const rangeDec = set
      ?.find()
      .find((d) => (d.spec as { key?: string }).key === `ai-${pending.id}-range`);
    return {
      from: rangeDec ? rangeDec.from : pending.from,
      to: rangeDec ? rangeDec.to : pending.to,
    };
  }

  async applyChunk(pending: TiptapPending, data: EditDeltaData): Promise<void> {
    if (isEmbedCanvasOp(data.payload)) {
      const canvasId = data.payload.canvasId;
      const canvasPending = pending as CanvasPending;
      // The placeholder embed was inserted on click (begin); reconcile it to the
      // real server canvasId. Move the pending flag from placeholder → real id so
      // the skeleton stays until the canvas row syncs into the workspace.
      markCanvasPending(canvasId);
      this.embedOps.reconcile(canvasPending.id, canvasId);
      clearCanvasPending(canvasPending.id);
      canvasPending.canvasId = canvasId;
      return;
    }

    if (!isReplaceTextOp(data.payload)) return;
    const textPending = pending as TextPending;
    textPending.text = data.payload.text;
    // Each chunk carries the full accumulated text. Per-token deltas from the stream
    // already pace the reveal, so write straight into the live range — no extra pacing.
    this.writeText(textPending, data.payload.text);
  }

  /**
   * Write `text` into the live target range (in-doc streaming, replace-as-it-goes).
   * Overwrites the original from the first call and grows in place. The range tracker
   * keeps [from,to] in sync across calls + concurrent edits. NOT addToHistory:false —
   * that would exclude the edit from undo (Yjs UndoManager.captureTransaction /
   * ProseMirror history honor it); the UndoManager's 500ms captureTimeout already
   * coalesces the rapid transactions into a single undo step.
   */
  private writeText(pending: TextPending, text: string): void {
    // Only render on a safe boundary: if this cumulative chunk ends mid-tag (e.g.
    // "...<stro"), hold it — don't show partial/literal tags. The next chunk that
    // completes the tag renders the whole thing. Plain text (no tags) is always safe.
    if (text && !isRenderableHtml(text)) return;
    // Replace the WHOLE written range [from, writtenEnd] with the full accumulated content
    // each chunk. ProseMirror's view diffs the result against the live doc (node.eq) and
    // reuses the DOM of unchanged leading blocks, so this is churn-free without any manual
    // stable-block bookkeeping. `from` is mapped live (concurrent edits before the range
    // shift it); `writtenEnd` is our tracked end of everything written so far.
    const from = this.liveRange(pending).from;
    const { state } = this.editor;
    const frag = text ? parseHtmlToFragment(this.editor, text) : null;
    const content =
      frag && frag.size > 0
        ? frag
        : text && !text.includes('<')
          ? Fragment.from(state.schema.text(text))
          : Fragment.empty;
    // Commit the doc replace + suggestion-decoration update + caret placement in ONE
    // transaction so the caret is never absent between dispatches.
    const tr = state.tr.replaceWith(from, pending.writtenEnd, content);
    // The inserted content occupies exactly [from, from + content.size]. Use that as the
    // write-end — NOT tr.mapping.map(writtenEnd), which biases past the content into the
    // trailing block boundary (+1), drifting the range and collapsing the caret to the doc
    // start over a multi-block stream ("stuck on the left").
    const newEnd = from + content.size;
    tr.setMeta(aiSuggestionPluginKey, { type: 'update', id: pending.id, text, streaming: true, from, to: newEnd });
    // Caret rides the nearest INLINE position at/before newEnd (resolved AGAINST tr.doc).
    const caretPos = inlinePosAtOrBefore(this.editor, newEnd, tr.doc);
    tr.setMeta(aiCaretPluginKey, { type: 'setCaret', pos: caretPos, state: 'writing', label: 'Nula' });
    if (!this.editor.isDestroyed) this.editor.view.dispatch(tr);
    pending.writtenEnd = newEnd;
    pending.to = newEnd;
  }

  /** Freeze the ghost preview (stop shimmer/caret) once streaming ends and the
   *  edit enters review. The orchestrator calls this when the stream resolves. */
  freezePreview(pending: TiptapPending): void {
    if (pending.kind !== 'text') return;
    getAiEditing(this.editor).update({ id: pending.id, text: pending.text, streaming: false, before: pending.originalText });
    // Streaming ended — leave the caret at the end briefly, then fade it out.
    this.lingerThenClearCaret();
  }

  accept(pending: TiptapPending): void {
    if (pending.kind === 'canvas') {
      // Option-2 commit: canvas entity + embed already exist; just clear bookkeeping.
      return;
    }

    // TextPending: the streamed text is already in the doc. Clear the decoration; leave the
    // caret at the end for a beat, then fade it out.
    getAiEditing(this.editor).clear(pending.id);
    this.lingerThenClearCaret();
  }

  reject(pending: TiptapPending): void {
    if (pending.kind === 'canvas') {
      // Generation failed before reconcile (canvasId still null): only the
      // placeholder node exists — remove it, nothing to delete server-side.
      if (!pending.canvasId) {
        clearCanvasPending(pending.id);
        this.embedOps.remove(pending.id);
        return;
      }
      clearCanvasPending(pending.canvasId);
      // Remove the embed NODE first: deleting the canvas entity synchronously fires a DELETED
      // event the node view reacts to — if the orphaned node is still in the doc, its position
      // resolution goes stale (RangeError / null targetNode). Remove the node, THEN the entity.
      this.embedOps.remove(pending.canvasId);
      this.workspace?.deleteDocPermanently(pending.canvasId).catch((err: unknown) => {
        console.error('Failed to delete canvas on reject', err);
        Sentry.captureException(err);
      });
      return;
    }
    if (pending.kind === 'text') {
      // A RequestingPending stub (tab-switched mid-stream, before begin() ran) reaches
      // here without originalSlice — nothing was written yet, so there's nothing to restore.
      if (!pending.originalSlice) {
        getAiEditing(this.editor).clear(pending.id);
        getAiEditing(this.editor).clearCaret();
        return;
      }
      // Restore the original SLICE (nodes + marks + block structure) over whatever was
      // streamed in — NOT flattened text, which would drop bold/headings/lists.
      if (this.editor.isDestroyed) return;
      const { from, to } = this.liveRange(pending);
      const { state } = this.editor;
      const tr = state.tr.replace(from, to, pending.originalSlice);
      this.editor.view.dispatch(tr);
      getAiEditing(this.editor).clear(pending.id);
      getAiEditing(this.editor).clearCaret();
      return;
    }
  }
}
